/**
 * Класс для управления вкладками авторизации.
 * Обрабатывает переключение между вкладками и отображение соответствующего контента.
 */
class AuthTabs {
  /** Контейнер вкладок */
  private container: HTMLElement
  /** Список элементов вкладок */
  private tabs: NodeListOf<HTMLElement>
  /** Список элементов контента вкладок */
  private contents: NodeListOf<HTMLElement>
  /** ID активной вкладки */
  private activeTab: string

  /**
   * Создает экземпляр AuthTabs.
   *
   * @param {HTMLElement} container - Контейнер с вкладками
   */
  constructor(container: HTMLElement) {
    this.container = container
    this.tabs = this.container.querySelectorAll('[data-auth-tabs-target="tab"]')
    this.contents = this.container.querySelectorAll(
      '[data-auth-tabs-target="content"]'
    )
    this.activeTab = this.container.dataset.authTabsDefaultValue || ''

    this.init()
  }

  /**
   * Инициализирует вкладки.
   * Добавляет обработчики событий и устанавливает активную вкладку.
   *
   * @private
   * @return {void}
   */
  private init(): void {
    this.attachEventListeners()
    this.updateActiveTab(this.activeTab)
  }

  /**
   * Добавляет обработчики событий для кнопок переключения вкладок.
   *
   * @private
   * @return {void}
   */
  private attachEventListeners(): void {
    const buttons = this.container.querySelectorAll(
      '[data-action*="auth-tabs#switchTab"]'
    )

    buttons.forEach((button) => {
      button.addEventListener('click', (e: Event) => {
        const target = e.currentTarget as HTMLElement
        const tabId = target.dataset.tab

        if (tabId) {
          this.switchTab(tabId)
        }
      })
    })
  }

  /**
   * Переключает на указанную вкладку.
   *
   * @public
   * @param {string} tabId - ID вкладки для переключения
   * @return {void}
   */
  public switchTab(tabId: string): void {
    this.activeTab = tabId
    this.updateActiveTab(tabId)
  }

  /**
   * Обновляет активную вкладку и отображение контента.
   * Добавляет/удаляет активные классы и показывает/скрывает контент.
   *
   * @private
   * @param {string} tabId - ID активной вкладки
   * @return {void}
   */
  private updateActiveTab(tabId: string): void {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tab === tabId
      tab.classList.toggle('auth-tabs__tab_active', isActive)
    })

    this.contents.forEach((content) => {
      const isActive = content.dataset.tab === tabId
      ;(content as HTMLElement).style.display = isActive ? 'block' : 'none'
    })
  }

  /**
   * Возвращает ID текущей активной вкладки.
   *
   * @public
   * @return {string} ID активной вкладки
   */
  public getActiveTab(): string {
    return this.activeTab
  }
}

/**
 * Инициализирует все контейнеры вкладок авторизации на странице.
 * Создает экземпляр AuthTabs для каждого найденного контейнера.
 *
 * @return {void}
 */
export function initAuthTabs(): void {
  const tabContainers = document.querySelectorAll(
    '[data-controller="auth-tabs"]'
  )

  tabContainers.forEach((container) => {
    new AuthTabs(container as HTMLElement)
  })
}
